import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Divider,
  Paper,
  styled,
  Link,
} from '@mui/material';
import JoinUsBox from '../../components/JoinUsBox';

const JoinSection = () => {
  return (
    <Box>
      {/* Intro Paragraph */}
      <Typography variant="homePageBody" paragraph>
        At <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>, we're
        reimagining what artificial intelligence can achieve. Our mission is to
        push the boundaries of human and machine collaboration, creating
        technologies that empower individuals, businesses, and communities
        around the world.
        <br />
        We believe AI should be intuitive, ethical, and transformative. To build
        it, we need passionate people like you curious minds, creative
        problem-solvers, and bold thinkers ready to make an impact.
      </Typography>

      {/* Section Heading */}
      <Typography variant="h3">
        Why Join <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>?
      </Typography>

      {/* Numbered List */}
      <Box>
        <Typography variant="homePageBody" fontWeight={600}>
          1. Work on World-Changing Technology
        </Typography>
        <Typography variant="homePageBody" paragraph>
          Join teams that are developing groundbreaking AI models, shaping the
          future of communication, creativity, and problem-solving.
        </Typography>
      </Box>

      <Box>
        <Typography variant="homePageBody" fontWeight={600}>
          2. Grow with Us
        </Typography>
        <Typography variant="homePageBody" paragraph>
          We invest in personal and professional development, offering learning
          programs, mentorship, and opportunities to explore new fields and
          skills.
        </Typography>
      </Box>

      <Box>
        <Typography variant="homePageBody" fontWeight={600}>
          3. A Culture of Innovation
        </Typography>
        <Typography variant="homePageBody" paragraph>
          We celebrate ideas. We challenge norms.{' '}
          <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> is a
          place where bold experimentation is encouraged and where your
          contributions truly matter.
        </Typography>
      </Box>

      <Box>
        <Typography variant="homePageBody" fontWeight={600}>
          4. Commitment to Ethics
        </Typography>
        <Typography variant="homePageBody" paragraph>
          We are committed to building AI responsibly, prioritizing
          transparency, safety, and fairness in every product we create.
        </Typography>
      </Box>

      <Box>
        <Typography variant="homePageBody" fontWeight={600}>
          5. Flexibility and Balance
        </Typography>
        <Typography variant="homePageBody" paragraph>
          Work where and how you perform best with flexible remote options,
          hybrid roles, and supportive environments that honor work-life
          balance.
        </Typography>
      </Box>
    </Box>
  );
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  paddingTop: '36px',
  paddingLeft: '1rem',
  paddingRight: '1rem',
  backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f7f7f7',
  color: '#909090',
}));

const LifeAtSection = () => {
  return (
    <Box>
      {/* Life at <span style={{fontFamily: 'SpaceGrotesk'}}>KaliyugAi</span> */}
      <Typography variant="h2" gutterBottom>
        Life at <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>
      </Typography>
      <Typography variant="homePageBody" paragraph>
        From engineering breakthroughs to design revolutions every{' '}
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> team
        member shares a commitment to excellence, curiosity and positive impact.{' '}
        <br />
        We foster diverse, inclusive teams where every voice is valued and every
        idea has the potential to inspire change.
      </Typography>
      <Typography variant="homePageBody" fontWeight={600} paragraph>
        "The future is built by those who dare to imagine it. At{' '}
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>, we’re
        turning imagination into reality."
      </Typography>

      {/* Current Opportunities */}
      <Box sx={{ marginTop: 'calc(100px)' }}>
        <Typography variant="h2" gutterBottom>
          Current Opportunities
        </Typography>
        <Typography variant="homePageBody" paragraph>
          Explore our open positions across Research, Engineering, Design,
          Product, Marketing, and more. <br />
          We hire globally and believe great ideas can come from anywhere.
        </Typography>
        <Typography variant="homePageBody" fontWeight={500} paragraph>
          🔍 Check Open Roles Below
        </Typography>

        <StyledPaper variant="outlined">
          <Typography
            variant="homePageBody"
            fontWeight={500}
            align="center"
            paragraph
          >
            We're not hiring at the moment, but we're always excited to connect
            with talented individuals.
            <br />
            Stay tuned for future opportunities!
          </Typography>
        </StyledPaper>
      </Box>

      {/* Internships */}
      <Box sx={{ marginTop: 'calc(100px)' }}>
        <Typography variant="h2">Internships and Early Careers</Typography>
        <Typography variant="homePageBody" paragraph>
          Starting out? We offers internships and entry-level opportunities to
          help you build real-world skills and work alongside some of the
          brightest minds in AI.
        </Typography>
        <Typography
          variant="homePageBody"
          fontWeight={600}
          align="center"
          paragraph
        >
          Write as at{' '}
          <Link href="mailto:support@kaliyugai.com" color="secondary">
            support@kaliyugai.com
          </Link>
        </Typography>
      </Box>

      {/* Hiring Process */}
      <Box sx={{ marginTop: 'calc(100px)' }}>
        <Typography variant="h2">Our Hiring Process</Typography>
        <Typography variant="homePageBody">
          We believe hiring should be a two-way conversation. Our process is
          designed to help you show your best self and for you to see if{' '}
          <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> is the
          right place to grow.
        </Typography>
        <Stack spacing={1} pl={2} mt={1}>
          {[
            '1. Application Review',
            '2. Initial Conversation',
            '3. Skills and Technical Assessment (where applicable)',
            '4. Final Interviews',
            '5. Offer & Onboarding 🚀',
          ].map((step, idx) => (
            <Typography variant="homePageBody" key={idx}>
              {step}
            </Typography>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

const Career = () => {
  return (
    <Box>
      <JoinSection />
      <Divider />
      <LifeAtSection />
      <JoinUsBox />
    </Box>
  );
};

export default Career;
